import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuthHook'
import { cn } from '../lib/utils'
import { Input } from '../components/ui/Input'
import { Button } from '../components/ui/Button'
import { useToast } from '../components/ui/useToast'

export function LoginPage() {
  const navigate = useNavigate()
  const { login, isAuthenticated, isLoading } = useAuth()
  const { notify } = useToast()
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    remember_me: false,
  })
  const [error, setError] = useState('')

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard')
    }
  }, [isAuthenticated, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    try {
      await login(formData)
  notify({ kind: 'success', title: 'Welcome back', message: 'Signed in successfully.' })
  navigate('/dashboard')
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.')
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-gray-50 dark:bg-gray-950">
      {/* Left: Branding */}
      <div className="relative hidden md:flex flex-col p-10 bg-gradient-to-br from-primary-600 via-primary-700 to-accent-600 text-white overflow-hidden">
        <div className="pointer-events-none absolute inset-0 gradient-mesh opacity-30" />
        {/* Top brand */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center shadow-soft-lg">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-white to-primary-100 flex items-center justify-center text-primary-600 font-bold text-lg">SM</div>
          </div>
          <div className="text-xl font-bold">studiomgr360</div>
        </div>
        {/* Centered hero content */}
        <div className="relative z-10 flex-1 flex items-center">
          <div className="max-w-md space-y-6">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight">Manage bookings, staff, and customers seamlessly.</h2>
            <p className="text-lg text-white/90 leading-relaxed">A fast, modern platform for multi-room studios. Secure, cloud-native, and built for teams.</p>
            <div className="flex items-center gap-6 pt-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-white"></div>
                <span className="text-sm text-white/80">Cloud-based</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-white"></div>
                <span className="text-sm text-white/80">Multi-tenant</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-white"></div>
                <span className="text-sm text-white/80">Secure</span>
              </div>
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className="relative z-10 text-sm text-white/70">© {new Date().getFullYear()} studiomgr360. All rights reserved.</div>
      </div>

      {/* Right: Form */}
      <div className="flex items-center justify-center px-6 py-12 md:px-12 bg-white dark:bg-gray-900">
        <div className="w-full max-w-md animate-fade-slide-up">
          <div className="mb-8">
            <div className="md:hidden text-center mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center mx-auto mb-3 shadow-soft">
                <span className="text-white font-bold text-lg">SM</span>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">studiomgr360</div>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Welcome back</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Sign in to your account to continue</p>
          </div>
          {error && (
            <div className="mb-6 bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/20 text-error-700 dark:text-error-400 px-4 py-3 rounded-xl">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="form-label">Email address</label>
              <Input id="email" name="email" type="email" required placeholder="Enter your email" value={formData.email} onChange={handleChange} />
            </div>
            <div>
              <label htmlFor="password" className="form-label">Password</label>
              <Input id="password" name="password" type="password" required placeholder="Enter your password" value={formData.password} onChange={handleChange} />
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input id="remember_me" name="remember_me" type="checkbox" className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-700 rounded dark:bg-gray-800" checked={formData.remember_me} onChange={handleChange} />
                Remember me
              </label>
              <a href="#" className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors">Forgot password?</a>
            </div>
            <Button type="submit" disabled={isLoading} className={cn('w-full h-12 text-base', isLoading && 'opacity-50 cursor-not-allowed')}>
              {isLoading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>
          <div className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
            Don't have an account?{' '}
            <Link to="/register" className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors">Sign up</Link>
          </div>
        </div>
      </div>
    </div>
  )
}